import React from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Icon from '@amzn/meridian/icon';

import { useClient, useGetAll } from '$/api';
import { CorePage, CorePageTitle, CoreSearchTable } from '$/components';
import { LocationAddButton, LocationColumnDefinitions } from './components';
import { RootUri } from '$/features/routes';

/* Bootstrap icons MIT license: https://github.com/twbs/icons/blob/main/LICENSE.md
The MIT License (MIT)
Copyright (c) 2019-2021 The Bootstrap Authors
Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
const locationSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-building" viewBox="0 0 16 16">
  <path fill-rule="evenodd" d="M14.763.075A.5.5 0 0 1 15 .5v15a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5V14h-1v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V10a.5.5 0 0 1 .342-.474L6 7.64V4.5a.5.5 0 0 1 .276-.447l8-4a.5.5 0 0 1 .487.022zM6 8.694 1 10.36V15h5V8.694zM7 15h2v-1.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5V15h2V1.309l-7 3.5V15z"/>
  <path d="M2 11h1v1H2v-1zm2 0h1v1H4v-1zm-2 2h1v1H2v-1zm2 0h1v1H4v-1zm4-4h1v1H8V9zm2 0h1v1h-1V9zm-2 2h1v1H8v-1zm2 0h1v1h-1v-1zm2-2h1v1h-1V9zm0 2h1v1h-1v-1zM8 7h1v1H8V7zm2 0h1v1h-1V7zm2 0h1v1h-1V7zM8 5h1v1H8V5zm2 0h1v1h-1V5zm2 0h1v1h-1V5zm0-2h1v1h-1V3z"/>
</svg>`;
const labSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-house-fill" viewBox="0 0 16 16">
<path fill-rule="evenodd" d="m8 3.293 6 6V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V9.293l6-6zm5-.793V6l-2-2V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5z"/>
<path fill-rule="evenodd" d="M7.293 1.5a1 1 0 0 1 1.414 0l6.647 6.646a.5.5 0 0 1-.708.708L8 2.207 1.354 8.854a.5.5 0 1 1-.708-.708L7.293 1.5z"/>
</svg>`;
const hostSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pc" viewBox="0 0 16 16">
<path d="M5 0a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1H5Zm.5 14a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1Zm2 0a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1ZM5 1.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5ZM5.5 3h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1Z"/>
</svg>`;
const deviceSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-usb-symbol" viewBox="0 0 16 16">
<path d="m7.792.312-1.533 2.3A.25.25 0 0 0 6.467 3H7.5v7.319a2.5 2.5 0 0 0-.515-.298L5.909 9.56A1.5 1.5 0 0 1 5 8.18v-.266a1.5 1.5 0 1 0-1 0v.266a2.5 2.5 0 0 0 1.515 2.298l1.076.461a1.5 1.5 0 0 1 .888 1.129 2.001 2.001 0 1 0 1.021-.006v-.902a1.5 1.5 0 0 1 .756-1.303l1.484-.848A2.5 2.5 0 0 0 11.995 7h.755a.25.25 0 0 0 .25-.25v-2.5a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25v2.5c0 .138.112.25.25.25h.741a1.5 1.5 0 0 1-.747 1.142L8.76 8.99a2.584 2.584 0 0 0-.26.17V3h1.033a.25.25 0 0 0 .208-.389L8.208.312a.25.25 0 0 0-.416 0Z"/>
</svg>`;

const locationIcon = {
  iconData: locationSvg,
  iconWidth: 24,
  iconHeight: 24,
};
const labIcon = {
  iconData: labSvg,
  iconWidth: 24,
  iconHeight: 24,
};
const hostIcon = {
  iconData: hostSvg,
  iconWidth: 24,
  iconHeight: 24,
};
const deviceIcon = {
  iconData: deviceSvg,
  iconWidth: 24,
  iconHeight: 24,
};

const filters = [
  { key: 'name', label: 'Name', isDefault: true },
  { key: 'description', label: 'Description' },
];

export const LocationListPage = (): JSX.Element => {
  const { lmslocations, lmslabs, hosts, devices } = useClient();

  const { data: dataLocations, isLoading: isLoadingLocations } = useGetAll(
    lmslocations.resourceKey,
    lmslocations.getAll,
    {}
  );
  const { data: dataLabs, isLoading: isLoadingLabs } = useGetAll(
    lmslabs.resourceKey,
    lmslabs.getAll,
    {}
  );
  const { data: dataHosts, isLoading: isLoadingHosts } = useGetAll(
    hosts.resourceKey,
    hosts.getAll,
    {}
  );
  const { data: dataDevices, isLoading: isLoadingDevices } = useGetAll(
    devices.resourceKey,
    devices.getAll,
    {}
  );

  /* eslint-disable prettier/prettier */
  return (
    <CorePage
      title="Locations"
      breadcrumbs={[{ title: 'LMS Lab Dashboard', href: RootUri }]}
      header={
        <>
          <Row widths={['fill', 'fit']}>
            <Column>
              <CorePageTitle title="Locations" />
            </Column>
            <Row alignmentHorizontal="right">
              <LocationAddButton />
            </Row>
          </Row>
        </>
      }
    >
      <Column spacing="large">
        <Column spacingInset="medium" type="outline">
          <Heading level={3}>Summary</Heading>

          {(!isLoadingLabs ||
            !isLoadingHosts ||
            !isLoadingDevices ||
            !isLoadingLocations) && (
            <Table headerColumns={1} showDividers showStripes>
              <TableRow>
                <TableCell>
                  <Icon tokens={locationIcon} />
                  Locations
                </TableCell>
                <TableCell>{dataLocations?.total}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Icon tokens={labIcon} />
                  Labs
                </TableCell>
                <TableCell>{dataLabs?.total}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Icon tokens={hostIcon} />
                  Agents
                </TableCell>
                <TableCell>{dataHosts?.total}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Icon tokens={deviceIcon} />
                  Devices
                </TableCell>
                <TableCell>{dataDevices?.total}</TableCell>
              </TableRow>
            </Table>
          )}
        </Column>
      </Column>

      <CoreSearchTable
        resourceKey={lmslocations.resourceKey}
        getAll={lmslocations.getAll}
        resourceFilters={filters}
        columns={LocationColumnDefinitions}
      />
    </CorePage>
  );
};
